"use client";
import { create } from "zustand";
import { SOCIAL_WALLET_CONFIG, SocialWalletConfig } from "@shared/src/config/wallet-config";
import { WalletClientSigner, SmartAccountSigner } from "@aa-sdk/core";
import { Connector } from "@shared/src/lib/types";
import { cookieManager } from "@shared/src/lib/auth/cookieManager";
import { useAdminAuthStore } from "@shared/src/stores/useAdminAuthStore";
import { getAuth, signOut as firebaseSignOut } from "firebase/auth";

import { AuthParams } from "@shared/src/hooks/auth/types";
import { AlchemySigner } from "@account-kit/core";
import { queryClient } from "@shared/src/providers/queryClientProvider";
import {} from "viem";
import { BackendAccount } from "@sdk/shared/types/settings.types";
import { authenticateWithSdk } from "@shared/src/functions/authenticateWithSdk";
import { useHostStore } from "./useHostStore";
import { useQuickTradeFlowStore } from "./useQuickTradeFlowStore";
export type AccountLinkingStatus = "initial" | "linking" | "success" | "error";

// Create ViewType from wallet IDs
export type ViewType = "social" | "all" | "account_linking" | "whitelist_check" | string;

export type WalletConnectionStatus =
  | "initial"
  | "awaiting_confirmation"
  | "siwe_confirmation"
  | "siwe_request"
  | "success"
  | "error";

export type SiweActions = {
  resolve: () => void;
  reject: () => void;
};

export type IxSigner = WalletClientSigner | SmartAccountSigner | AlchemySigner;

interface ViewConfig {
  title: string;
  showBack: boolean;
}

// Create VIEW_CONFIG dynamically
export const VIEW_CONFIG: Record<ViewType, ViewConfig> = {
  social: { title: "Social Login", showBack: false },
  all: { title: "All Wallets", showBack: true },
  account_linking: { title: "Account Linking", showBack: true },
  whitelist_check: { title: "Whitelist Required", showBack: true },
} as Record<ViewType, ViewConfig>;

export type Account = {
  id: number;
  type: "sca" | "eoa";
  address: `0x${string}`;
  isAuthenticated: boolean;
  email?: string;
  signer: IxSigner;
  username: string;
  jwt: string;
  connectorIcon: string;
  backendAccount: BackendAccount;
  authParams: AuthParams; // Ensure authParams is always present
  isAutoTrader?: boolean;
};

export type SerialisableValue = string | boolean | number | undefined | null;
//@dev this is here to prevent serialisation errors
export type SerialisableAccount = {
  id: number;
  type: "sca" | "eoa";
  address: `0x${string}`;
  isAuthenticated: boolean;
  email?: string;
  jwt?: string;
  signer: undefined;
  connectorIcon: string;
  authParams: Record<string, SerialisableValue | SerialisableValue[] | Record<string, SerialisableValue>>;
};

let g: AuthParams;

// Add TempAuthData interface
export interface TempAuthData extends Omit<Account, "username" | "backendAccount"> {
  username?: string;
  jwt: string;
  authParams: AuthParams; // Ensure authParams is always present
  backendAccount?: BackendAccount;
}

export interface REOWNWalletState {
  isWalletSelected: boolean;
  isConnectionComplete: boolean;
  walletName: string;
  walletIcon: string;
}
export interface AuthModalState extends REOWNWalletState {
  isNewAccount: boolean;
  walletStatus: WalletConnectionStatus;
  accountLinkingStatus: AccountLinkingStatus | null;
  selectedWalletId: string | null;
  connectionLink: string | null;
  isExistingAccount: boolean;
  selectedView: ViewType;
  viewHistory: ViewType[];
  tempAuth: TempAuthData | null;
  isVerifying: boolean;
  showBackButton: boolean;
  modalOpen: boolean;
  showModalSidebar: boolean;
  justConnected: boolean;
}

export interface AuthStore extends AuthModalState {
  isInitialised: boolean;
  setIsInitialised: (isInitialised: boolean) => void;
  alchemyOauthPrepared: boolean;
  setAlchemyOauthPrepared: (prepared: boolean) => void;
  connectedAccounts: Account[];
  activeAccount: Account | null;
  setActiveAccount: (account: Account, keepModalOpen?: boolean) => Promise<void>; // Remove separate authParams parameter
  disconnectAccount: (account: Account) => void;
  siweActions: SiweActions | null;

  email: string;
  setEmail: (email: string) => void;

  // Wallet Connection

  setIsVerifying: (isVerifying: boolean) => void;
  setShowBackButton: (show: boolean) => void;
  setWalletStatus: (status: WalletConnectionStatus, walletName: string) => void;
  setAccountLinkingStatus: (status: AccountLinkingStatus) => void;
  setSelectedWalletId: (id: string | null) => void;
  setConnectionLink: (link: string | null) => void;
  setIsExistingAccount: (isExisting: boolean) => void;
  setModalOpen: (isOpen: boolean) => void;
  setSelectedView: (view: ViewType) => void;
  goBack: () => void;
  setSiweActions: (actions: SiweActions) => void;

  // Wallet Data
  socialWallets: SocialWalletConfig[];
  getWalletById: (id: string, connectors: readonly Connector[]) => SocialWalletConfig | Connector | undefined;

  // Reset Functions
  resetEmail: () => void;
  closeAndResetAuthModal: () => void;
  resetAuthModal: () => void;
  signOut: (disconnect?: () => any) => void;

  // Add tempAuth state
  setTempAuth: (data: TempAuthData) => void;
  clearTempAuth: () => void;
}

// Helper function to format view titles
const formatViewTitle = (view: string) => {
  if (view === "social") return "Sign In";
  if (view === "all") return "All Wallets";
  if (view === "account_linking") return "Account Linking";
  if (view === "whitelist_check") return "Whitelist Required";
  return view
    .split(/[-_]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Initial state definition organized into logical groups
const initialState = {
  // Account-related state
  accountState: {
    activeAccount: null as Account | null,
    connectedAccounts: [] as Account[],
    email: "",
  },

  // Auth Modal UI state
  uiState: {
    modalOpen: false,
    showModalSidebar: true,
    isNewAccount: false,
    isVerifying: false,
    showBackButton: false,
  },

  // Wallet Connection state
  walletState: {
    walletStatus: "initial" as WalletConnectionStatus,
    accountLinkingStatus: null as AccountLinkingStatus | null,
    selectedWalletId: null as string | null,
    connectionLink: null as string | null,
    isExistingAccount: false,
    siweActions: null,
    walletIcon: "",
    walletName: "",
    justConnected: false,
  },

  // View Navigation state
  navigationState: {
    selectedView: "social" as ViewType,
    viewHistory: ["social"] as ViewType[],
  },

  // Temporary Auth state
  tempAuthState: {
    tempAuth: null as TempAuthData | null,
  },

  // Wallet data state
  walletDataState: {
    socialWallets: SOCIAL_WALLET_CONFIG,
  },
};

const serialiseAccount = (serializAbleAccount: SerialisableAccount) => {
  console.log("serializing account", serializAbleAccount);
  const serializedData = JSON.stringify(serializAbleAccount, (key, value: unknown) => {
    if (
      key === "signer" ||
      key === "connector" ||
      typeof value === "function" ||
      (typeof value === "object" && value !== null && value.constructor?.name === "Promise") ||
      value instanceof BigInt ||
      typeof value === "bigint"
    ) {
      return undefined;
    }
    return value;
  });

  return serializedData;
};

export const useAuthStore = create<AuthStore>((set, get) => {
  return {
    // Spread all initial state properties correctly
    ...initialState.accountState,
    ...initialState.uiState,
    ...initialState.walletState,
    ...initialState.navigationState,
    ...initialState.tempAuthState,
    ...initialState.walletDataState,
    alchemyOauthPrepared: false,
    isInitialised: false,
    setAlchemyOauthPrepared: prepared => set({ alchemyOauthPrepared: prepared }),
    isWalletSelected: false,
    isConnectionComplete: false,
    setModalOpen: (isOpen: boolean) => set({ modalOpen: isOpen }),
    setIsInitialised: (isInitialised: boolean) => set({ isInitialised }),
    setActiveAccount: async (incomingAccount: Account, keepModalOpen = false, connector?: Connector) => {
      await authenticateWithSdk({
        address: incomingAccount.address,
        accountType: incomingAccount.type,
        signer: incomingAccount.signer as AlchemySigner,
        jwt: incomingAccount.jwt,
        provider: incomingAccount.authParams.provider,
        connector: incomingAccount.authParams.connector,
      });

      return set(state => {
        const account = { ...incomingAccount, email: incomingAccount.email || state.email };
        console.log("setting active account", account, connector);
        // Check if account is already in connectedAccounts
        const isExistingAccount = state.connectedAccounts.some(acc => acc.address === account.address);

        // Create a serializable version of the account before storing
        const serialisableAuthParams = {
          ...account.authParams,
          signer: undefined,
          connector: undefined,
          onSuccess: undefined,
          provider: undefined,
        } satisfies Record<string, SerialisableValue>;

        const serializAbleAccount = {
          ...account,
          signer: undefined,
          authParams: serialisableAuthParams,
        } satisfies SerialisableAccount;

        // Store in localStorage
        localStorage.setItem("activeAccount", serialiseAccount(serializAbleAccount));
        cookieManager().setAuthToken(account.jwt, account.address);

        // If not, add it
        if (!isExistingAccount) {
          const newConnectedAccounts = [...state.connectedAccounts, account];
          // const serializedAccounts = serialiseAccount(newConnectedAccounts);
          // localStorage.setItem("activeAccounts", serializedAccounts);

          if (!keepModalOpen) get().closeAndResetAuthModal();

          useHostStore.getState().setConnectorId(account.authParams.connectorId);

          return {
            activeAccount: account,
            connectedAccounts: newConnectedAccounts,
          };
        }
        return {
          activeAccount: account,
        };
      });
    },

    setSiweActions: (actions: SiweActions) => set({ siweActions: actions }),
    setEmail: email => set({ email }),
    setIsVerifying: isVerifying =>
      set(state => {
        // Update view config when entering/exiting verification
        VIEW_CONFIG["social"] = {
          ...VIEW_CONFIG["social"],
          title: isVerifying ? "Email Verification" : "Sign In",
          showBack: isVerifying,
        };

        return {
          isVerifying,
        };
      }),
    setShowBackButton: show => set({ showBackButton: show }),

    // Wallet Connection
    setWalletStatus: (status: WalletConnectionStatus, walletName) =>
      set(state => {
        const currentView = state.selectedView;
        let title = walletName || "All Wallets";
        let showBack = false;

        if (currentView === "social") {
          title = status === "awaiting_confirmation" ? walletName : "Sign in";
          showBack = status === "awaiting_confirmation"; // Show back button only in awaiting confirmation for social
        } else if (currentView === "all") {
          title = "All Wallets";
          showBack = true;
        } else if (currentView === "account_linking") {
          title = "Account Linking";
          showBack = true;
        } else if (currentView === "whitelist_check") {
          title = "Whitelist Required";
          showBack = true;
        } else {
          switch (status) {
            case "initial":
            case "awaiting_confirmation":
            case "success":
            case "error":
              title = walletName;
              showBack = true;
              break;
          }
        }

        VIEW_CONFIG[currentView as ViewType] = {
          ...VIEW_CONFIG[currentView as ViewType],
          title: formatViewTitle(title),
          showBack,
        };

        return {
          walletStatus: status,
        };
      }),
    setAccountLinkingStatus: status => set({ accountLinkingStatus: status }),
    setSelectedWalletId: id => set({ selectedWalletId: id }),
    setConnectionLink: link => set({ connectionLink: link }),
    setIsExistingAccount: isExisting => set({ isExistingAccount: isExisting }),

    // Wallet Data
    getWalletById: (id: string, connectors: readonly Connector[]) => {
      // First check social wallets
      const socialWallet = get().socialWallets.find(wallet => wallet.id === id);
      if (socialWallet) return socialWallet;

      // Then check connectors
      return [...connectors].find(connector => connector.id === id);
    },

    // View Configuration & Navigation
    setSelectedView: view =>
      set(state => {
        let title = "Sign In";
        if (view === "all") {
          title = "All Wallets";
        } else if (view === "account_linking") {
          title = "Account Linking";
        } else if (view !== "social") {
          title = "Wallet";
        }

        VIEW_CONFIG[view] = {
          ...VIEW_CONFIG[view],
          title: formatViewTitle(title),
          showBack: view === "account_linking" ? false : view !== "social" || state.isVerifying,
        };

        // Reset wallet status when changing views
        if (view !== state.selectedView) {
          get().setWalletStatus("initial", formatViewTitle(title));
        }

        // Handle view history based on navigation flow
        if (view === "social") {
          // Social is always the root view
          return {
            selectedView: view,
            viewHistory: ["social"],
            walletStatus: "initial",
          };
        }

        if (view === "account_linking") {
          // Account linking is a terminal view - no back navigation
          return {
            selectedView: view,
            viewHistory: ["account_linking"],
            walletStatus: "initial",
          };
        }

        if (view === "all") {
          // All wallets view always comes after social
          return {
            selectedView: view,
            viewHistory: ["social", "all"],
            walletStatus: "initial",
          };
        }

        // For specific wallet views
        if (state.selectedView === "all") {
          // Coming from all wallets view
          return {
            selectedView: view,
            viewHistory: ["social", "all", view],
            walletStatus: "initial",
          };
        }

        // For direct wallet selection from social view
        if (state.selectedView === "social") {
          return {
            selectedView: view,
            viewHistory: ["social", view],
            walletStatus: "initial",
          };
        }

        // Default case - add to history if it's a new view
        if (!state.viewHistory.includes(view)) {
          return {
            selectedView: view,
            viewHistory: [...state.viewHistory, view],
            walletStatus: "initial",
          };
        }

        return {
          selectedView: view,
          walletStatus: "initial",
        };
      }),
    goBack: () =>
      set(state => {
        if (state.viewHistory.length <= 1) {
          // If we're at the root (social), stay there
          return state;
        }

        const newHistory = [...state.viewHistory];
        newHistory.pop(); // Remove current view
        const previousView = newHistory[newHistory.length - 1];

        // Reset states based on navigation flow
        if (state.selectedView === "social" && state.walletStatus === "awaiting_confirmation") {
          // When going back from social login awaiting confirmation
          return {
            selectedView: "social",
            viewHistory: newHistory,
            walletStatus: "initial",
          };
        } else if (previousView === "social") {
          // When going back to social view from other views
          return {
            selectedView: previousView,
            viewHistory: newHistory,
            walletStatus: "initial",
            selectedWalletId: null,
            connectionLink: null,
            isExistingAccount: false,
          };
        } else if (state.selectedView !== "social" && state.selectedView !== "all" && previousView === "all") {
          // When going back to all wallets view
          return {
            selectedView: previousView,
            viewHistory: newHistory,
            walletStatus: "initial",
            selectedWalletId: null,
            connectionLink: null,
            isExistingAccount: false,
          };
        }

        // For other navigation cases, maintain the current wallet status
        return {
          selectedView: previousView,
          viewHistory: newHistory,
        };
      }),

    // Reset Functions
    resetEmail: () =>
      set({
        email: "",
        isVerifying: false,
        showBackButton: false,
      }),
    closeAndResetAuthModal: () => {
      console.log("reseting AuthModal");
      useQuickTradeFlowStore.getState().setShowSignInModal(false);
      get().clearTempAuth();
      set({
        ...initialState.uiState,
        ...initialState.walletState,
        ...initialState.navigationState,
        ...initialState.tempAuthState,
      });
    },
    resetAuthModal: () => {
      console.log("reseting AuthModal");
      get().clearTempAuth();
      set({
        ...initialState.uiState,
        modalOpen: true,
        ...initialState.walletState,
        ...initialState.navigationState,
        ...initialState.tempAuthState,
      });
    },
    signOut: async disconnect => {
      cookieManager().removeAuth(); // Remove auth cookie
      localStorage.removeItem("activeAccount");
      localStorage.removeItem("activeAccounts");
      const activeAccount = get().activeAccount;
      const authParams = activeAccount?.authParams;
      disconnect?.();

      if (authParams?.type === "wallet") authParams?.connector?.disconnect();
      if ("disconnect" in (activeAccount?.signer ?? {})) (activeAccount?.signer as AlchemySigner).disconnect();
      // Sign out admin as well
      useAdminAuthStore.getState().clearAdminUser();
      try {
        await firebaseSignOut(getAuth());
      } catch (e) {
        // ignore firebase signout errors
      }
      // queryClient.clear();
      set(useAuthStore.getInitialState(), true);
    },
    disconnectAccount: (account: Account) => {
      cookieManager().removeAuth();

      const connectedAccounts = get().connectedAccounts;
      if (connectedAccounts.length === 1) {
        get().signOut();
        return;
      }
      const newConnectedAccounts = connectedAccounts.filter(acc => acc.address !== account.address);
      // const serializedAccounts = serialiseAccount(newConnectedAccounts);
      // localStorage.setItem("activeAccounts", serializedAccounts);
      get().setActiveAccount(newConnectedAccounts[0]);

      set({
        connectedAccounts: newConnectedAccounts,
      });
    },

    // Add tempAuth state and actions
    setTempAuth: data => {
      if (data.jwt) cookieManager().setAuthToken(data.jwt, data.address);
      return set(state => ({
        tempAuth: { ...state.tempAuth, ...data },
      }));
    },
    clearTempAuth: () =>
      set({
        tempAuth: initialState.tempAuthState.tempAuth,
      }),
  };
});
